import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDocumentTabs } from '../lib/useDocumentTabs'

/**
 * 글로벌 문서 탭 — 문서 상세 라우트(DocumentView)에서만 노출.
 * 라우트 :uuid 가 활성 탭. 활성 탭이 가시 영역 밖이면 자동 scrollIntoView.
 * 스크롤바는 숨기되, 좌/우 끝 화살표 버튼으로 가시화.
 * 우클릭 — 컨텍스트 메뉴 (Close / Close Others / Close to the Right / Refresh).
 */
export function TabBar() {
  const { uuid } = useParams<{ uuid?: string }>()
  const { tabs, close, closeOthers, closeToRight } = useDocumentTabs()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const barRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLAnchorElement | null>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)
  const [menu, setMenu] = useState<{ x: number; y: number; uuid: string } | null>(null)

  /** 스크롤 위치 / 폭 측정해 좌·우 화살표 가시 여부 결정 */
  const measure = () => {
    const el = barRef.current
    if (!el) return
    const epsilon = 1
    setShowLeft(el.scrollLeft > epsilon)
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon)
  }

  useEffect(() => {
    const el = barRef.current
    if (!el) return
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', measure); ro.disconnect() }
  }, [tabs.length])

  // 활성 탭 변경 시 가시 영역으로 자동 스크롤
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' })
    }
    const t = setTimeout(measure, 250)
    return () => clearTimeout(t)
  }, [uuid, tabs.length])

  // 컨텍스트 메뉴 외부 클릭 / Esc 로 닫기
  useEffect(() => {
    if (!menu) return
    const onDoc = () => setMenu(null)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  if (tabs.length === 0) return null

  const scrollBy = (dx: number) => {
    barRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }

  const closeTab = (tabUuid: string) => {
    const t = tabs.find((x) => x.uuid === tabUuid)
    if (!t) return
    if (t.dirty && !window.confirm(`'${t.title}' has unsaved changes. Close anyway?`)) return
    const isActive = tabUuid === uuid
    const nextUuid = close(tabUuid)
    if (isActive) navigate(nextUuid ? `/d/${nextUuid}` : '/groups')
  }

  const handleCloseOthers = (tabUuid: string) => {
    const others = tabs.filter((t) => t.uuid !== tabUuid)
    const dirtyCount = others.filter((t) => t.dirty).length
    if (dirtyCount > 0 && !window.confirm(`${dirtyCount} other tab(s) have unsaved changes. Close anyway?`)) return
    closeOthers(tabUuid)
    if (uuid !== tabUuid) navigate(`/d/${tabUuid}`)
  }

  const handleCloseToRight = (tabUuid: string) => {
    const idx = tabs.findIndex((t) => t.uuid === tabUuid)
    if (idx < 0) return
    const right = tabs.slice(idx + 1)
    const dirtyCount = right.filter((t) => t.dirty).length
    if (dirtyCount > 0 && !window.confirm(`${dirtyCount} tab(s) to the right have unsaved changes. Close anyway?`)) return
    closeToRight(tabUuid)
    // 활성이 오른쪽 영역에 있었다면 pivot 으로 이동
    if (uuid && right.some((t) => t.uuid === uuid)) navigate(`/d/${tabUuid}`)
  }

  const handleRefresh = (tabUuid: string) => {
    qc.invalidateQueries({ queryKey: ['documents', tabUuid] })
  }

  return (
    <div className="doc-tab-bar-wrap">
      {showLeft && (
        <button type="button" className="doc-tab-scroll-btn left"
                aria-label="Scroll tabs left" title="Scroll left"
                onClick={() => scrollBy(-200)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
      )}
      <div ref={barRef} className="doc-tab-bar" role="tablist" aria-label="Open documents">
        {tabs.map((t) => {
          const isActive = t.uuid === uuid
          const tabHref = `/d/${t.uuid}`
          return (
            <Link key={t.uuid} to={tabHref}
              ref={isActive ? activeRef : undefined}
              role="tab" aria-selected={isActive}
              className={`doc-tab${isActive ? ' active' : ''}`}
              data-doc-uuid={t.uuid}
              title={t.title}
              onContextMenu={(e) => {
                e.preventDefault()
                setMenu({ x: e.clientX, y: e.clientY, uuid: t.uuid })
              }}>
              <span className="doc-tab-label">{t.dirty ? '• ' : ''}{t.title}</span>
              <button type="button" className="doc-tab-close"
                aria-label={`Close ${t.title}`}
                title="Close"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeTab(t.uuid) }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                </svg>
              </button>
            </Link>
          )
        })}
      </div>
      {showRight && (
        <button type="button" className="doc-tab-scroll-btn right"
                aria-label="Scroll tabs right" title="Scroll right"
                onClick={() => scrollBy(200)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </button>
      )}
      {menu && (
        <ul className="doc-tab-menu" role="menu"
            style={{ position: 'fixed', left: menu.x, top: menu.y, zIndex: 100 }}
            onMouseDown={(e) => e.stopPropagation()}>
          <li role="menuitem" onClick={() => { closeTab(menu.uuid); setMenu(null) }}>Close</li>
          <li role="menuitem" onClick={() => { handleCloseOthers(menu.uuid); setMenu(null) }}>Close Others</li>
          <li role="menuitem" onClick={() => { handleCloseToRight(menu.uuid); setMenu(null) }}>Close to the Right</li>
          <li role="menuitem" onClick={() => { handleRefresh(menu.uuid); setMenu(null) }}>Refresh</li>
        </ul>
      )}
    </div>
  )
}
